import React, { useState } from "react";

import { SilenceRequest } from "@energy-advice/alertfan-api-client/dist/lib/generated/api";
import { T } from "services/lang/LanguageService";
import { useAddSilence } from "api/api-hooks/alert-fan-hooks";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import useCurrentUser from "hooks/useCurrentUser";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { addToDate, DweenTimeUnitEnum } from "utils/time-utils";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";

type SilenceCreateProps = {
  showExpired: boolean;
  tags?: { topics?: Array<string>; items?: Array<string> };

  dialog: boolean;
  showDialog: (val: boolean) => void;
};

const SilenceCreate = ({ dialog, showDialog, showExpired, tags }: SilenceCreateProps) => {
  const [itemTagChips, setItemTagChips] = useState<Array<string>>(tags?.items || []);
  const [topicTagChips, setTopicTagChips] = useState<Array<string>>(tags?.topics || []);
  const [dateFrom, setDateFrom] = useState(new Date());
  const [dateTo, setDateTo] = useState(addToDate(new Date(), 1, DweenTimeUnitEnum.DAYS));
  const [comment, setComment] = useState("");

  const { currentUser } = useCurrentUser();

  const { mutate } = useAddSilence(showExpired, {
    onSuccess: () => {
      showDialog(false);
      easasAlert({
        summary: T("alert.success"),
        severity: "success",
        detail: T("alert.silence.silence-was-created"),
        life: 3000,
      });
    },
    onFailure: err => {
      easasAlert({ summary: T("alert.error"), severity: "error", detail: err, life: 3000 });
    },
  });

  const onSilenceCreateButtonClick = () => {
    const tempSilenceRule: SilenceRequest = {
      itemTags: itemTagChips,
      topicTags: topicTagChips,
      startsAt: dateFrom.toISOString(),
      endsAt: dateTo.toISOString(),
      createdBy: currentUser?.username || "unknown",
      comment,
    };

    mutate(tempSilenceRule);
  };

  return (
    <Dialog
      onHide={() => showDialog(false)}
      visible={dialog}
      header={T("action.add-silence")}
      style={{ width: "450px" }}
      resizable={false}
    >
      <div className="flex flex-column gap-2">
        <div className="flex flex-column">
          <b>{T("word.item-tags")}</b>
          <div className="p-fluid">
            <Chips value={itemTagChips} onChange={e => setItemTagChips(e.value)} addOnBlur />
          </div>
        </div>
        <div className="flex flex-column">
          <b>{T("word.topic-tags")}</b>
          <div className="p-fluid">
            <Chips value={topicTagChips} onChange={e => setTopicTagChips(e.value)} addOnBlur />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex flex-column">
            <b>{T("word.starts-at")}</b>
            <CalendarWrapper value={dateFrom} onChange={setDateFrom} />
          </div>
          <div className="flex flex-column">
            <b>{T("word.ends-at")}</b>
            <CalendarWrapper value={dateTo} onChange={setDateTo} />
          </div>
        </div>
        <div className="flex flex-column">
          <b>{T("word.author")}</b>
          <InputText value={currentUser?.username} disabled />
        </div>
        <div className="flex flex-column">
          <b>{T("word.comment")}</b>
          <InputTextarea
            rows={3}
            cols={30}
            value={comment}
            onChange={e => setComment(e.target.value)}
            autoResize
          />
        </div>
        <Button label={T("action.create")} onClick={() => onSilenceCreateButtonClick()} />
      </div>
    </Dialog>
  );
};

export default SilenceCreate;
